import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  FormControl,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import {
  useGetV2AdminOrganizationsByOrganizationIdQuery,
  type SourceProviderSchema,
} from '../store/hooks';

interface ProviderMappingPanelProps {
  organizationId: string;
  /** Distinct source providers referenced by the staged batch. */
  providers: SourceProviderSchema[];
  /** source provider key -> platform user id. */
  value: Record<string, string>;
  onChange: (mapping: Record<string, string>) => void;
  /** Whether encounters are in the current commit selection (drives the gate). */
  encounterSelected: boolean;
}

/** "encounter ×3, patient_diagnosis ×5" — what references this provider. */
function describeCounts(counts: SourceProviderSchema['counts']): string {
  const entries = Object.entries(counts ?? {});
  if (entries.length === 0) return '—';
  return entries.map(([type, n]) => `${type} ×${n}`).join(', ');
}

/**
 * The commit-gate's provider mapping. Each distinct source provider in the
 * batch (e.g. `provider:npi:42`) is resolved to a platform user; encounters
 * commit owned by that clinician rather than the operator. Providers marked
 * "required" are referenced by encounters and must be mapped before those
 * encounters can commit — an unmapped one blocks the row server-side.
 */
export function ProviderMappingPanel({
  organizationId,
  providers,
  value,
  onChange,
  encounterSelected,
}: ProviderMappingPanelProps) {
  // The org's members — the same source the Members tab shows — are the users
  // an encounter can be attributed to. (The legacy /providers endpoint only
  // surfaces a subset, so we read membership off the org detail.)
  const { data: organization, isLoading } =
    useGetV2AdminOrganizationsByOrganizationIdQuery({ organizationId });

  const options = (organization?.users ?? []).filter(
    u => u.status !== 'inactive',
  );

  const setOne = (providerKey: string, userId: string) => {
    const next = { ...value };
    if (userId) next[providerKey] = userId;
    else delete next[providerKey];
    onChange(next);
  };

  const unmappedRequired = providers.filter(
    p => p.required && !value[p.provider_key],
  );

  if (providers.length === 0) return null;

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle2" gutterBottom>
        Map providers ({providers.length})
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Resolve each source provider to a user in this organization. Encounters
        commit owned by the mapped clinician, never the operator.
      </Typography>

      {!isLoading && encounterSelected && unmappedRequired.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {unmappedRequired.length} provider
          {unmappedRequired.length === 1 ? '' : 's'} with encounters{' '}
          {unmappedRequired.length === 1 ? 'is' : 'are'} unmapped. Map{' '}
          {unmappedRequired.length === 1 ? 'it' : 'them'} to commit those
          encounters.
        </Alert>
      )}

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <CircularProgress size={20} />
        </Box>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Source provider</TableCell>
              <TableCell>Referenced by</TableCell>
              <TableCell>Maps to</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {providers.map(p => {
              const needed = encounterSelected && p.required;
              const missing = needed && !value[p.provider_key];
              return (
                <TableRow key={p.provider_key} hover>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                    {p.provider_key}
                    {p.required && (
                      <Chip
                        size="small"
                        label="required"
                        color={missing ? 'warning' : 'default'}
                        sx={{ ml: 1 }}
                      />
                    )}
                  </TableCell>
                  <TableCell sx={{ fontSize: 12 }}>
                    {describeCounts(p.counts)}
                  </TableCell>
                  <TableCell>
                    <FormControl size="small" sx={{ minWidth: 240 }} error={missing}>
                      <Select
                        displayEmpty
                        value={value[p.provider_key] ?? ''}
                        onChange={e => setOne(p.provider_key, e.target.value)}
                        inputProps={{
                          'aria-label': `Map ${p.provider_key} to a user`,
                        }}
                      >
                        <MenuItem value="">
                          <em>Unmapped</em>
                        </MenuItem>
                        {options.map(o => {
                          const label = o.name || o.email || o.user_id;
                          const roles = (o.roles ?? []).join(', ');
                          return (
                            <MenuItem key={o.user_id} value={o.user_id}>
                              {roles ? `${label} — ${roles}` : label}
                            </MenuItem>
                          );
                        })}
                      </Select>
                    </FormControl>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </Box>
  );
}
